import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";

/** Returns `value` only after it has stopped changing for `delay` ms.
 *
 * Used to keep a fast-updating value (e.g. a range slider being dragged) from
 * driving an expensive effect — e.g. a live "Aplicar (N)" count query — on
 * every intermediate change. The visible UI still uses the immediate value;
 * only the debounced copy feeds the query. Debouncing is delegated to lodash. */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  const update = useMemo(() => debounce(setDebounced, delay), [delay]);

  useEffect(() => {
    update(value);
  }, [value, update]);

  // Drop any pending update on unmount / delay change.
  useEffect(() => () => update.cancel(), [update]);

  return debounced;
}
